import React, { useCallback, useRef, useState } from "react";
import {
  Callout,
  DirectionalHint,
  IconButton,
  PrimaryButton,
  DefaultButton,
  Stack,
  Text,
} from "@fluentui/react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { SerializedFilter } from "../../store/tableFilters";
import { TextFilterInput } from "./filters/TextFilterInput";
import { NumberFilterInput } from "./filters/NumberFilterInput";
import { DateFilterInput } from "./filters/DateFilterInput";
import { BooleanFilterToggle } from "./filters/BooleanFilterToggle";
import { TaxonomyFilterSelect } from "./filters/TaxonomyFilterSelect";
import {
  createDefaultTextFilter,
  createDefaultNumberFilter,
  createDefaultDateFilter,
  createDefaultBooleanFilter,
  createDefaultTaxonomyFilter,
} from "./hooks/useTableFilters";

export type FilterPopoverConfig = {
  type: SerializedFilter["type"];
  // For taxonomy filters
  taxonomyKey?: string;
  exclude?: string[];
  customOptionLabel?: (tax: any) => string;
  // For number filters
  min?: number;
  max?: number;
  // For boolean filters
  trueLabel?: string;
  falseLabel?: string;
  // Common
  placeholder?: string;
};

type FilterPopoverProps = {
  columnKey: string;
  columnLabel: string;
  filterConfig: FilterPopoverConfig;
  currentFilter?: SerializedFilter;
  isFiltered: boolean;
  onApplyFilter: (filter: SerializedFilter) => void;
  onClearFilter: () => void;
  targetRef?: React.RefObject<HTMLElement>;
};

export const FilterPopover = ({
  columnKey,
  columnLabel,
  filterConfig,
  currentFilter,
  isFiltered,
  onApplyFilter,
  onClearFilter,
  targetRef,
}: FilterPopoverProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [localFilter, setLocalFilter] = useState<SerializedFilter | undefined>(
    currentFilter,
  );

  const getDefaultFilter = useCallback((): SerializedFilter => {
    switch (filterConfig.type) {
      case "text":
        return createDefaultTextFilter();
      case "number":
        return createDefaultNumberFilter();
      case "date":
        return createDefaultDateFilter();
      case "boolean":
        return createDefaultBooleanFilter();
      case "taxonomy":
        return createDefaultTaxonomyFilter(filterConfig.taxonomyKey || "");
      default:
        return createDefaultTextFilter();
    }
  }, [filterConfig]);

  const handleToggle = useCallback(() => {
    if (!isOpen) {
      setLocalFilter(currentFilter || getDefaultFilter());
    }
    setIsOpen(!isOpen);
  }, [isOpen, currentFilter, getDefaultFilter]);

  const handleApply = useCallback(() => {
    if (localFilter) {
      onApplyFilter(localFilter);
    }
    setIsOpen(false);
  }, [localFilter, onApplyFilter]);

  const handleClear = useCallback(() => {
    onClearFilter();
    setLocalFilter(getDefaultFilter());
    setIsOpen(false);
  }, [onClearFilter, getDefaultFilter]);

  const handleDismiss = useCallback(() => {
    setIsOpen(false);
  }, []);

  const renderFilterInput = () => {
    if (!localFilter) return null;

    switch (filterConfig.type) {
      case "text":
        return (
          <TextFilterInput
            value={localFilter as any}
            onChange={setLocalFilter}
            placeholder={filterConfig.placeholder}
          />
        );
      case "number":
        return (
          <NumberFilterInput
            value={localFilter as any}
            onChange={setLocalFilter}
            min={filterConfig.min}
            max={filterConfig.max}
          />
        );
      case "date":
        return (
          <DateFilterInput
            value={localFilter as any}
            onChange={setLocalFilter}
          />
        );
      case "boolean":
        return (
          <BooleanFilterToggle
            value={localFilter as any}
            onChange={setLocalFilter}
            trueLabel={filterConfig.trueLabel}
            falseLabel={filterConfig.falseLabel}
          />
        );
      case "taxonomy":
        return (
          <TaxonomyFilterSelect
            value={localFilter as any}
            onChange={setLocalFilter}
            taxonomyKey={filterConfig.taxonomyKey || ""}
            exclude={filterConfig.exclude}
            customOptionLabel={filterConfig.customOptionLabel}
          />
        );
      default:
        return null;
    }
  };

  const target = targetRef?.current || buttonRef.current;

  return (
    <>
      <FilterButtonContainer ref={buttonRef}>
        <IconButton
          iconProps={{
            iconName: isFiltered ? "FilterSolid" : "Filter",
          }}
          onClick={handleToggle}
          title={t("ram.table.filter.filterColumn")}
          styles={{
            root: {
              width: 24,
              height: 24,
              color: isFiltered ? "#0078d4" : "inherit",
            },
            rootHovered: {
              color: "#0078d4",
            },
            icon: {
              fontSize: 12,
            },
          }}
        />
      </FilterButtonContainer>

      {isOpen && target && (
        <Callout
          target={target}
          onDismiss={handleDismiss}
          directionalHint={DirectionalHint.bottomLeftEdge}
          gapSpace={4}
          isBeakVisible={false}
          layerProps={{
            styles: {
              root: {
                zIndex: 1000000,
              },
            },
          }}
          styles={{
            root: {
              padding: 16,
              minWidth: 250,
              maxWidth: 320,
              zIndex: 1000000,
              backgroundColor: "white",
              boxShadow:
                "0 3.2px 7.2px 0 rgba(0,0,0,0.132), 0 0.6px 1.8px 0 rgba(0,0,0,0.108)",
            },
            calloutMain: {
              overflow: "visible",
              backgroundColor: "white",
            },
          }}
        >
          <Stack tokens={{ childrenGap: 12 }}>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
              {t("ram.table.filter.filterBy")} {columnLabel}
            </Text>

            {renderFilterInput()}

            <Stack horizontal tokens={{ childrenGap: 8 }}>
              <PrimaryButton
                text={t("ram.table.filter.apply")}
                onClick={handleApply}
                styles={{ root: { flex: 1 } }}
              />
              <DefaultButton
                text={t("ram.table.filter.clear")}
                onClick={handleClear}
                disabled={!isFiltered}
                styles={{ root: { flex: 1 } }}
              />
            </Stack>
          </Stack>
        </Callout>
      )}
    </>
  );
};

const FilterButtonContainer = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export default FilterPopover;
